import { pageWindow } from './pageWindow';

// Σελιδοποίηση με **αριθμούς**, στη θέση της «Φόρτωσης περισσότερων» (ΜΛΑ-06).
//
// Το κουμπί «φόρτωσε κι άλλα» επιλέχθηκε ως ενδιάμεσο γιατί ταιριάζει με ροή
// στηλών μεταβλητού ύψους, αλλά **δεν κλιμακώνεται**: με 500 πόρους απαιτεί 25
// πατήματα για να φτάσει κανείς στο τέλος, και δεν υπάρχει τρόπος να πάει
// κάποιος κατευθείαν στη σελίδα 12 — ούτε να τη μοιραστεί.
//
// Οι αριθμοί είναι σύνδεσμοι της κατάστασης και όχι ενέργειες: πατώντας τους
// αλλάζει η **διεύθυνση**, οπότε η σελίδα 12 έχει δική της διεύθυνση που
// επιβιώνει σε ανανέωση και στέλνεται σε συνάδελφο.

function PageButton({ isActive, isDisabled, label, onPress, children }) {
    return (
        <button
            type="button"
            disabled={isDisabled}
            aria-label={label}
            // Το `aria-current="page"` είναι ο τρόπος που η υποστηρικτική
            // τεχνολογία μαθαίνει ποια σελίδα διαβάζεται. Χρώμα μόνο δεν αρκεί.
            aria-current={isActive ? 'page' : undefined}
            onClick={onPress}
            className={[
                'inline-flex h-10 min-w-10 items-center justify-center rounded-full px-3 text-sm font-medium',
                'transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                isDisabled ? 'cursor-not-allowed text-muted opacity-40' : '',
                isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted hover:bg-accent-soft hover:text-accent-soft-foreground'
            ].join(' ')}
        >
            {children}
        </button>
    );
}

/**
 * @param {object} meta Το `meta` της απόκρισης (D4): `{ page, limit, total, totalPages }`.
 * @param {(page: number) => void} onPage Καλείται με τη νέα σελίδα.
 * @param {string} noun Ο πληθυντικός για τη γραμμή μέτρησης.
 */
export function Pagination({ meta, onPage, noun = 'εγγραφές' }) {
    if (!meta || meta.total === 0) return null;

    const { page, totalPages, total } = meta;
    const window = pageWindow(page, totalPages);

    return (
        <nav aria-label="Σελιδοποίηση" className="flex flex-col items-center gap-2 py-6">
            {/* Το σύνολο έρχεται από το `meta` και αφορά το **φιλτραρισμένο**
                ερώτημα. Όσο η αναζήτηση γινόταν στον πελάτη, ο αριθμός αυτός
                ήταν το σύνολο της βάσης και το μήνυμα ήταν παραπλανητικό. */}
            <p className="text-sm text-muted" aria-live="polite">
                {total} {noun}
                {totalPages > 1 && ` — σελίδα ${page} από ${totalPages}`}
            </p>

            {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-center gap-1">
                    <PageButton
                        isDisabled={page <= 1}
                        label="Προηγούμενη σελίδα"
                        onPress={() => onPage(page - 1)}
                    >
                        <i className="fa-solid fa-chevron-left" aria-hidden="true" />
                    </PageButton>

                    {window.map((value, index) =>
                        value === '…' ? (
                            // Δεν είναι κουμπί και δεν διαβάζεται: είναι ένδειξη συνέχειας.
                            <span
                                key={`gap-${index}`}
                                aria-hidden="true"
                                className="px-1 text-sm text-muted"
                            >
                                …
                            </span>
                        ) : (
                            <PageButton
                                key={value}
                                isActive={value === page}
                                label={`Σελίδα ${value}`}
                                onPress={() => onPage(value)}
                            >
                                {value}
                            </PageButton>
                        )
                    )}

                    <PageButton
                        isDisabled={page >= totalPages}
                        label="Επόμενη σελίδα"
                        onPress={() => onPage(page + 1)}
                    >
                        <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                    </PageButton>
                </div>
            )}
        </nav>
    );
}

export default Pagination;
